import {
  MAX_MIN_FEE_EUR,
  MAX_SUCCESS_FEE_PCT,
  type PensionFundTier,
  type PricingPlanTier,
} from '@carsfinders/contracts';
import { useCallback, useEffect, useId, useMemo, useState } from 'react';

import { ConfirmDialog } from '../../components/confirm-dialog';
import { ApiError } from '../../lib/api';
import { t } from '../../lib/i18n';
import * as edits from './pricing-edits';
import {
  buildPlatformFeePreview,
  buildPreview,
  fetchCurrentRules,
  publishRules,
  validateRules,
  type PlatformFeeCell,
  type PreviewRow,
} from './pricing-preview';
import type { PricingRuleSet } from '@carsfinders/contracts';

const EUR_FORMAT = new Intl.NumberFormat('uk-UA', { maximumFractionDigits: 2 });
const PCT_FORMAT = new Intl.NumberFormat('uk-UA', { maximumFractionDigits: 2 });

/**
 * An EUR tariff figure shown in CHF (owner, 2026-10-12: prices in CHF across
 * the site, admin included) at the rule set's own `chfToEurRate`. The plan
 * itself stays EUR — that is what the inputs above configure.
 */
const formatChfFromEur = (amountEur: number, chfToEurRate: number): string =>
  EUR_FORMAT.format(Math.round(chfToEurRate > 0 ? amountEur / chfToEurRate : amountEur));

/** A fraction as the percentage the owner reads and types: 0.045 → 4.5. */
const toPercent = (fraction: number): number => Number((fraction * 100).toFixed(4));

/** A number box's value; a cleared box is NaN (refused by validation), never a silent 0. */
const numberFrom = (raw: string): number => (raw.trim() === '' ? Number.NaN : Number(raw));

/** What a controlled number box shows — React refuses `NaN` as a value. */
const boxValue = (value: number): number | '' => (Number.isFinite(value) ? value : '');

const tierName = (tier: PricingPlanTier): string => t(`admin.tiers.${tier}`);

/**
 * Admin tariffs page (Phase 7 — SC-8).
 *
 * `PUT /api/admin/pricing/rules` has existed since Phase 8 with no screen in
 * front of it, so the only way to change a commission was to edit the seed
 * constant and ship a deploy. That is what this page removes.
 *
 * Two things about it are load-bearing rather than cosmetic:
 *
 *  1. The preview is the POINT. Five EUR control prices per tier (SC-1), each
 *     showing the fee, whether the tier minimum bound («мін.») and the base it
 *     is measured on («6% від €10 000»), with a tier the publish invariant
 *     refuses in red. The old grid charged EUR 975 on a CHF 1,500 lot and
 *     nobody noticed, because nobody was looking at a share.
 *
 * The grid is a percentage with a minimum per tier (volume-tier commission,
 * SC-11): only `successFeePct` and `minFee` are editable. A legacy document has
 * its flat part and monthly fee zeroed on load (`clearLegacyFees`), with a
 * one-line notice. The volume-tier thresholds are `tierRules` on the same
 * document — a toggle (off = `null`, the rollback) and four numbers.
 *
 *  2. Saving is gated CLIENT-SIDE by the same `findTariffViolation` the API
 *     refuses with. Not as the guard — the API's 422 is the guard — but so the
 *     owner learns what breaks the grid while they are still editing it, rather
 *     than from a rejected request.
 *
 * Publishing is an INSERT of a new immutable version, so `customs.version` must
 * be bumped; a re-used version answers 409 and is surfaced as such.
 *
 * It must also carry a NEW `customs.validFrom` (re-review, N1). The first
 * version of this page bumped the version alone, so a published grid inherited
 * the incumbent's effective date — and `findActiveRuleSet` picks the newest
 * `validFrom` with no defined order among ties. The owner signed the tariff,
 * pressed Publish, saw "published", and the API went on billing the old grid
 * with no error anywhere. The effective-date field and its blocker are the half
 * of that fix the operator can see; `ACTIVE_RULE_SET_SORT` in the API is the
 * half that makes the tie impossible.
 */
export function PricingPlansPage() {
  const [rules, setRules] = useState<PricingRuleSet | null>(null);
  const [version, setVersion] = useState('');
  /**
   * WHEN the edited grid takes effect (`customs.validFrom`), as the
   * `datetime-local` input holds it. Page state rather than an edit on `rules`,
   * exactly like `version`: `rules.customs.validFrom` has to stay the
   * INCUMBENT's date, because that is what a publish must get past.
   */
  const [validFromLocal, setValidFromLocal] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  /**
   * «Use recommended grid» pending confirmation — it overwrites all three tiers
   * and the volume-tier rules in one click, so it is gated the same way
   * `minimum-bid-control.tsx` gates its one-click change: nothing server-side
   * happens here (Publish still owns that), but the ON-SCREEN overwrite is
   * otherwise silent and undoable only by reloading before Publish.
   */
  const [confirmingRecommended, setConfirmingRecommended] = useState(false);
  /** Tiers whose legacy flat part / monthly fee was zeroed on load — the notice. */
  const [legacyCleared, setLegacyCleared] = useState<PricingPlanTier[]>([]);
  const volumeHeadingId = useId();

  useEffect(() => {
    let cancelled = false;
    fetchCurrentRules()
      .then((loaded) => {
        if (cancelled) return;
        // Neither field is editable any more and the publish invariant refuses
        // both, so a version composed from a legacy document starts with them
        // at 0 — and says so, rather than leaving an unfixable violation.
        const composed = edits.clearLegacyFees(loaded);
        setRules(composed.rules);
        setLegacyCleared(composed.clearedTiers);
        setVersion(loaded.customs.version);
        // Defaults to NOW, not to the incumbent's date: a tariff the owner has
        // just signed off is in force from the moment they publish it, and
        // inheriting the loaded date is precisely the tie that made the previous
        // version of this page a no-op (N1).
        setValidFromLocal(edits.toDateTimeLocal(new Date()));
      })
      .catch((error: unknown) => {
        if (!cancelled) setLoadError(error instanceof Error ? error.message : String(error));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const preview: PreviewRow[] = useMemo(
    () => (rules ? buildPreview(rules.plans) : []),
    [rules],
  );
  const validation = useMemo(
    () => (rules ? validateRules(rules) : { ok: true, message: null }),
    [rules],
  );
  const platformPreview: PlatformFeeCell[] = useMemo(
    () => (rules ? buildPlatformFeePreview(rules) : []),
    [rules],
  );
  /**
   * Why this publish would not take effect — a re-used version (409) or an
   * effective date that does not get past the incumbent's (N1). Separate from
   * `validation`, which judges the DOCUMENT; this judges the PUBLISH.
   */
  const publishBlocker = useMemo(
    () => (rules ? edits.findPublishBlocker(rules, { version, validFromLocal }) : null),
    [rules, version, validFromLocal],
  );
  const publishBlockerMessage = useMemo(() => {
    if (!publishBlocker) return null;
    if (publishBlocker.code === 'VERSION_UNCHANGED') return t('admin.pricing.versionMustChange');
    if (publishBlocker.code === 'VALID_FROM_INVALID') return t('admin.pricing.validFromRequired');
    return t('admin.pricing.validFromMustAdvance', {
      date: publishBlocker.incumbentValidFrom.toLocaleString('uk-UA'),
    });
  }, [publishBlocker]);

  const setBandPct = useCallback(
    (tier: PricingPlanTier, index: number, pct: number) => {
      setSaved(false);
      setRules((current) => (current ? edits.setBandPct(current, tier, index, pct) : current));
    },
    [],
  );

  const setBandUpTo = useCallback((tier: PricingPlanTier, index: number, upTo: number | null) => {
    setSaved(false);
    setRules((current) => (current ? edits.setBandUpTo(current, tier, index, upTo) : current));
  }, []);

  /**
   * Edit any leaf of the loaded document.
   *
   * The page used to own one setter per band field and therefore re-published
   * `swissExport`, `fx` and `customs.pensionFundTiers` verbatim — which made the
   * documented `ua-2026-01 -> ua-2026-02` migration impossible through the only
   * screen that exists for it (Phase 7 review, C3b). Every edit is a pure
   * function in `pricing-edits.ts`, so the claim that the migration is
   * performable here is testable without a DOM.
   */
  const patchSwissExport = useCallback((patch: Partial<PricingRuleSet['swissExport']>) => {
    setSaved(false);
    setRules((current) => (current ? edits.patchSwissExport(current, patch) : current));
  }, []);

  const patchFx = useCallback((patch: Partial<PricingRuleSet['fx']>) => {
    setSaved(false);
    setRules((current) => (current ? edits.patchFx(current, patch) : current));
  }, []);

  const setPensionTier = useCallback((index: number, patch: Partial<PensionFundTier>) => {
    setSaved(false);
    setRules((current) => (current ? edits.setPensionTier(current, index, patch) : current));
  }, []);

  const setPlanSuccessFeePct = useCallback((tier: PricingPlanTier, fraction: number) => {
    setSaved(false);
    setRules((current) =>
      current ? edits.setPlanSuccessFeePct(current, tier, fraction) : current,
    );
  }, []);

  const setPlanMinFee = useCallback((tier: PricingPlanTier, minFee: number) => {
    setSaved(false);
    setRules((current) => (current ? edits.setPlanMinFee(current, tier, minFee) : current));
  }, []);

  const setVolumeTiersEnabled = useCallback((enabled: boolean) => {
    setSaved(false);
    setRules((current) => (current ? edits.setVolumeTiersEnabled(current, enabled) : current));
  }, []);

  const patchTierRules = useCallback((patch: edits.TierRulesPatch) => {
    setSaved(false);
    setRules((current) => (current ? edits.patchTierRules(current, patch) : current));
  }, []);

  const applyRecommendedGrid = useCallback(() => {
    setSaved(false);
    setRules((current) => (current ? edits.applyRecommendedGrid(current) : current));
  }, []);

  /**
   * Which platform-fee parameterization is in force — CHOSEN, not inferred from
   * an emptied input (N4).
   */
  const setPlatformFeeModel = useCallback((model: edits.PlatformFeeModel) => {
    setSaved(false);
    setRules((current) => (current ? edits.setPlatformFeeModel(current, model) : current));
  }, []);

  const handleSave = useCallback(async () => {
    if (!rules) return;
    setSaveError(null);
    setSaved(false);

    if (!validation.ok) {
      setSaveError(validation.message);
      return;
    }
    // Not a nag on either count: a re-used version is a 409 every time, and a
    // `validFrom` that does not get past the incumbent's publishes a grid that
    // may never become active — which used to be a SILENT no-op (N1).
    if (publishBlocker) {
      setSaveError(publishBlockerMessage);
      return;
    }
    const validFrom = edits.parseDateTimeLocal(validFromLocal);
    if (!validFrom) {
      setSaveError(t('admin.pricing.validFromRequired'));
      return;
    }

    setIsSaving(true);
    try {
      await publishRules(edits.buildPublishPayload(rules, version, validFrom));
      setSaved(true);
    } catch (error: unknown) {
      const message =
        error instanceof ApiError
          ? ((error.body as { error?: string } | undefined)?.error ?? error.message)
          : error instanceof Error
            ? error.message
            : String(error);
      setSaveError(message);
    } finally {
      setIsSaving(false);
    }
  }, [rules, validation, version, validFromLocal, publishBlocker, publishBlockerMessage]);

  if (loadError) {
    return <p className="p-6 text-cf-danger">{loadError}</p>;
  }
  if (!rules) {
    return <p className="p-6 text-cf-ink-muted">{t('admin.common.loading')}</p>;
  }

  return (
    <div className="space-y-6 p-4 lg:p-6">
      <header className="space-y-1">
        <h1 className="text-xl font-semibold">{t('admin.pricing.title')}</h1>
        <p className="text-sm text-cf-ink-muted">{t('admin.pricing.subtitle')}</p>
      </header>

      {/* ── The grid: a percentage with a minimum per tier ─────────────────── */}
      <section className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-cf-ink-muted">
            {t('admin.pricing.feeRule', {
              ceiling: MAX_SUCCESS_FEE_PCT * 100,
              maxMin: MAX_MIN_FEE_EUR,
            })}
          </p>
          <button
            type="button"
            data-testid="pricing-recommended"
            onClick={() => setConfirmingRecommended(true)}
            className="rounded border border-cf-line px-3 py-1 text-sm focus-visible:outline-2 focus-visible:outline-cf-ink"
          >
            {t('admin.pricing.recommended')}
          </button>
        </div>
        {legacyCleared.length > 0 && (
          <p role="status" data-testid="pricing-legacy-cleared" className="text-sm text-cf-ink-muted">
            {t('admin.pricing.legacyFeesCleared', {
              tiers: legacyCleared.map(tierName).join(', '),
            })}
          </p>
        )}
        {(Object.keys(rules.plans) as PricingPlanTier[]).map((tier) => {
          const plan = rules.plans[tier];
          const bands = plan.marginalBands ?? [];
          return (
            <div key={tier} className="rounded-lg border border-cf-line bg-cf-surface p-4">
              <h2 className="mb-3 font-medium">{tierName(tier)}</h2>
              <div className="mb-3 flex flex-wrap items-center gap-4 text-sm">
                <label className="text-cf-ink-muted">
                  <span className="sr-only">{tierName(tier)} </span>
                  {t('admin.pricing.successFeePct')}
                  <input
                    type="number"
                    step="0.1"
                    min={0}
                    max={MAX_SUCCESS_FEE_PCT * 100}
                    data-testid={`plan-${tier}-successFeePct`}
                    className="ml-2 w-24 rounded border border-cf-line px-2 py-1"
                    value={boxValue(toPercent(plan.successFeePct))}
                    onChange={(event) =>
                      setPlanSuccessFeePct(tier, numberFrom(event.target.value) / 100)
                    }
                  />
                </label>
                <label className="text-cf-ink-muted">
                  <span className="sr-only">{tierName(tier)} </span>
                  {t('admin.pricing.minFee')}
                  <input
                    type="number"
                    step="1"
                    min={0}
                    max={MAX_MIN_FEE_EUR}
                    data-testid={`plan-${tier}-minFee`}
                    className="ml-2 w-24 rounded border border-cf-line px-2 py-1"
                    value={boxValue(plan.minFee)}
                    onChange={(event) => setPlanMinFee(tier, numberFrom(event.target.value))}
                  />
                </label>
              </div>
              <div className="space-y-2">
                {bands.map((band, index) => (
                  <div key={index} className="flex flex-wrap items-center gap-3 text-sm">
                    <label className="text-cf-ink-muted">
                      {t('admin.pricing.upTo')}
                      <input
                        type="number"
                        aria-label={`${tier} band ${index} upTo`}
                        className="ml-2 w-32 rounded border border-cf-line px-2 py-1"
                        value={band.upTo ?? ''}
                        placeholder="∞"
                        onChange={(event) =>
                          setBandUpTo(
                            tier,
                            index,
                            event.target.value === '' ? null : Number(event.target.value),
                          )
                        }
                      />
                    </label>
                    <label className="text-cf-ink-muted">
                      {t('admin.pricing.pct')}
                      <input
                        type="number"
                        step="0.001"
                        aria-label={`${tier} band ${index} pct`}
                        className="ml-2 w-28 rounded border border-cf-line px-2 py-1"
                        value={band.pct}
                        onChange={(event) => setBandPct(tier, index, Number(event.target.value))}
                      />
                    </label>
                    <span className="text-xs text-cf-ink-subtle">
                      {(band.pct * 100).toFixed(2)}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </section>

      {/* ── Volume tiers: `tierRules`, off = null (the rollback) ─────────────── */}
      <section
        aria-labelledby={volumeHeadingId}
        className="space-y-3 rounded-lg border border-cf-line bg-cf-surface p-4"
      >
        <h2 id={volumeHeadingId} className="font-medium">
          {t('admin.pricing.volumeTitle')}
        </h2>
        <label className="flex w-fit items-center gap-2 text-sm">
          <input
            type="checkbox"
            data-testid="volume-tiers-enabled"
            checked={rules.tierRules !== null}
            onChange={(event) => setVolumeTiersEnabled(event.target.checked)}
          />
          {t('admin.pricing.volumeEnabled')}
        </label>
        {rules.tierRules ? (
          <div className="flex flex-wrap items-center gap-4 text-sm">
            {(
              [
                ['windowDays', rules.tierRules.windowDays, 'admin.pricing.windowDays'],
                ['holdDays', rules.tierRules.holdDays, 'admin.pricing.holdDays'],
                ['dealer', rules.tierRules.thresholds.dealer, 'admin.pricing.thresholdDealer'],
                [
                  'dealer_pro',
                  rules.tierRules.thresholds.dealer_pro,
                  'admin.pricing.thresholdDealerPro',
                ],
              ] as const
            ).map(([field, value, labelKey]) => (
              <label key={field} className="text-cf-ink-muted">
                {t(labelKey)}
                <input
                  type="number"
                  step="1"
                  min={field === 'holdDays' ? 0 : 1}
                  data-testid={`tier-rules-${field}`}
                  className="ml-2 w-24 rounded border border-cf-line px-2 py-1"
                  value={boxValue(value)}
                  onChange={(event) => patchTierRules({ [field]: numberFrom(event.target.value) })}
                />
              </label>
            ))}
          </div>
        ) : (
          <p className="text-sm text-cf-ink-subtle">{t('admin.pricing.volumeOff')}</p>
        )}
      </section>

      {/* ── Swiss leg: the platform fee and the FX rate (C3b) ──────────────── */}
      <section className="space-y-3 rounded-lg border border-cf-line bg-cf-surface p-4">
        <h2 className="font-medium">{t('admin.pricing.swissTitle')}</h2>
        {/* Owner decision 2026-09-28: the platform fee is our internal cost. */}
        <p className="text-sm text-cf-ink-muted">{t('admin.pricing.swissFeeInternalHint')}</p>
        <div className="flex flex-wrap items-center gap-4 text-sm">
          {/*
           * The fee MODEL is picked, never inferred (N4). Clearing the
           * percentage box used to write `null`, which drops
           * `resolveAuctionFeeChf` onto the flat `exportFeeChf` — a different
           * commercial model, reached by deleting three characters, with
           * nothing on screen saying the model had changed.
           */}
          <label className="text-cf-ink-muted">
            {t('admin.pricing.feeModel')}
            <select
              aria-label="platform fee model"
              className="ml-2 rounded border border-cf-line px-2 py-1"
              value={edits.platformFeeModel(rules)}
              onChange={(event) =>
                setPlatformFeeModel(event.target.value as edits.PlatformFeeModel)
              }
            >
              <option value="percentage">{t('admin.pricing.feeModelPercentage')}</option>
              <option value="flat">{t('admin.pricing.feeModelFlat')}</option>
            </select>
          </label>
          <label className="text-cf-ink-muted">
            {t('admin.pricing.auctionFeePct')}
            <input
              type="number"
              step="0.001"
              aria-label="auctionFeePct"
              className="ml-2 w-28 rounded border border-cf-line px-2 py-1 disabled:opacity-50"
              disabled={edits.platformFeeModel(rules) === 'flat'}
              value={rules.swissExport.auctionFeePct ?? ''}
              placeholder={t('admin.pricing.flatInstead')}
              onChange={(event) =>
                patchSwissExport({
                  // Emptying the box is a rate of ZERO, not a silent switch to
                  // the flat fee — the selector above owns that choice.
                  auctionFeePct: event.target.value === '' ? 0 : Number(event.target.value),
                })
              }
            />
          </label>
          <label className="text-cf-ink-muted">
            {t('admin.pricing.auctionFeeMinChf')}
            <input
              type="number"
              aria-label="auctionFeeMinChf"
              className="ml-2 w-28 rounded border border-cf-line px-2 py-1"
              value={rules.swissExport.auctionFeeMinChf}
              onChange={(event) =>
                patchSwissExport({ auctionFeeMinChf: Number(event.target.value) })
              }
            />
          </label>
          <label className="text-cf-ink-muted">
            {t('admin.pricing.exportFeeChf')}
            <input
              type="number"
              aria-label="exportFeeChf"
              className="ml-2 w-28 rounded border border-cf-line px-2 py-1"
              value={rules.swissExport.exportFeeChf}
              onChange={(event) => patchSwissExport({ exportFeeChf: Number(event.target.value) })}
            />
          </label>
          <label className="text-cf-ink-muted">
            {t('admin.pricing.chfToEurRate')}
            <input
              type="number"
              step="0.01"
              aria-label="chfToEurRate"
              className="ml-2 w-28 rounded border border-cf-line px-2 py-1"
              value={rules.swissExport.chfToEurRate}
              onChange={(event) => patchSwissExport({ chfToEurRate: Number(event.target.value) })}
            />
          </label>
          <label className="text-cf-ink-muted">
            {t('admin.pricing.uahPerEur')}
            <input
              type="number"
              step="0.1"
              aria-label="uahPerEur"
              className="ml-2 w-28 rounded border border-cf-line px-2 py-1"
              value={rules.fx.uahPerEur}
              onChange={(event) => patchFx({ uahPerEur: Number(event.target.value) })}
            />
          </label>
        </div>

        {/* Live preview of the Swiss leg — CHF lots, labelled CHF. */}
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-cf-ink-muted">
              <th className="py-1 pr-4">{t('admin.pricing.lotChf')}</th>
              {platformPreview.map((cell) => (
                <th key={cell.priceChf} className="py-1 pr-4">
                  CHF {cell.priceChf}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-cf-line-soft">
              <td className="py-2 pr-4 font-medium">{t('admin.pricing.platformFee')}</td>
              {platformPreview.map((cell) => (
                <td
                  key={cell.priceChf}
                  data-testid={`platform-fee-${cell.priceChf}`}
                  className="py-2 pr-4 font-mono"
                >
                  CHF {Math.round(cell.feeChf)}{' '}
                  <span className="text-xs">({(cell.share * 100).toFixed(1)}%)</span>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </section>

      {/* ── Pension-fund ladder, UAH thresholds (C3b) ───────────────────────── */}
      <section className="space-y-2 rounded-lg border border-cf-line bg-cf-surface p-4">
        <h2 className="font-medium">{t('admin.pricing.pensionTitle')}</h2>
        {rules.customs.pensionFundTiers.map((tier, index) => (
          <div key={index} className="flex flex-wrap items-center gap-3 text-sm">
            <label className="text-cf-ink-muted">
              {t('admin.pricing.upToUah')}
              <input
                type="number"
                aria-label={`pension tier ${index} upToUah`}
                className="ml-2 w-40 rounded border border-cf-line px-2 py-1"
                value={tier.upToUah ?? ''}
                placeholder="∞"
                onChange={(event) =>
                  setPensionTier(index, {
                    upToUah: event.target.value === '' ? null : Number(event.target.value),
                  })
                }
              />
            </label>
            <label className="text-cf-ink-muted">
              {t('admin.pricing.pct')}
              <input
                type="number"
                step="0.001"
                aria-label={`pension tier ${index} pct`}
                className="ml-2 w-28 rounded border border-cf-line px-2 py-1"
                value={tier.pct}
                onChange={(event) => setPensionTier(index, { pct: Number(event.target.value) })}
              />
            </label>
            <span className="text-xs text-cf-ink-subtle">
              {tier.upToUah
                ? `≈ CHF ${formatChfFromEur(tier.upToUah / rules.fx.uahPerEur, rules.swissExport.chfToEurRate)}`
                : '≈ ∞'}
            </span>
          </div>
        ))}
      </section>

      {/* ── Preview: fee, «мін.», and the base it is measured on ─────────────── */}
      <section className="overflow-x-auto rounded-lg border border-cf-line bg-cf-surface p-4">
        <h2 className="mb-3 font-medium">{t('admin.pricing.previewTitle')}</h2>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-cf-ink-muted">
              <th className="py-1 pr-4">{t('admin.pricing.tier')}</th>
              {preview[0]?.cells.map((cell) => (
                <th key={cell.price} className="py-1 pr-4">
                  CHF {formatChfFromEur(cell.price, rules.swissExport.chfToEurRate)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {preview.map((row) => (
              <tr key={row.tier} className="border-t border-cf-line-soft">
                <th scope="row" className="py-2 pr-4 text-left font-medium">
                  {tierName(row.tier)}
                </th>
                {row.cells.map((cell) => (
                  <td
                    key={cell.price}
                    data-testid={`preview-${row.tier}-${cell.price}`}
                    className={`py-2 pr-4 font-mono ${cell.tierRefused ? 'text-cf-danger font-bold' : ''}`}
                  >
                    CHF {formatChfFromEur(cell.fee, rules.swissExport.chfToEurRate)}
                    {cell.atMinimum && (
                      <span className="ml-1 text-xs">{t('admin.pricing.previewMin')}</span>
                    )}
                    <span className="block text-xs text-cf-ink-subtle">
                      {t('admin.pricing.previewBase', {
                        pct: `${PCT_FORMAT.format(cell.share * 100)}%`,
                        price: formatChfFromEur(cell.price, rules.swissExport.chfToEurRate),
                      })}
                    </span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-3 text-xs text-cf-ink-subtle">{t('admin.pricing.previewLegend')}</p>
      </section>

      {/* ── Publish ────────────────────────────────────────────────────────── */}
      <section className="flex flex-wrap items-center gap-3 rounded-lg border border-cf-line bg-cf-surface p-4">
        <label className="text-sm text-cf-ink-muted">
          {t('admin.pricing.newVersion')}
          <input
            aria-label="new version"
            className="ml-2 w-48 rounded border border-cf-line px-2 py-1"
            value={version}
            onChange={(event) => setVersion(event.target.value)}
          />
        </label>
        {/*
         * WHEN it takes effect. Without this the page published the incumbent's
         * own `validFrom`, so the new grid TIED with the one it was meant to
         * replace and might never become active — "published" on screen, the old
         * commission on the invoice (N1).
         */}
        <label className="text-sm text-cf-ink-muted">
          {t('admin.pricing.validFrom')}
          <input
            type="datetime-local"
            aria-label="valid from"
            className="ml-2 w-56 rounded border border-cf-line px-2 py-1"
            value={validFromLocal}
            onChange={(event) => setValidFromLocal(event.target.value)}
          />
        </label>
        <span className="text-xs text-cf-ink-subtle">
          {t('admin.pricing.validFromIncumbent', {
            version: rules.customs.version,
            date: rules.customs.validFrom.toLocaleString('uk-UA'),
          })}
        </span>
        <button
          type="button"
          data-testid="pricing-publish"
          onClick={() => void handleSave()}
          disabled={isSaving || !validation.ok || publishBlocker !== null}
          className="rounded bg-cf-accent px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {isSaving ? t('admin.common.saving') : t('admin.pricing.publish')}
        </button>
        {/*
         * The SERVER's message, from the same `findTariffViolation` the PUT
         * refuses with (422) — shown while the owner is still editing, next to
         * the button it disables.
         */}
        {!validation.ok && (
          <span role="alert" data-testid="pricing-violation" className="text-sm text-cf-danger">
            {validation.message}
          </span>
        )}
        {publishBlockerMessage && !saveError && (
          <span className="text-sm text-cf-danger">{publishBlockerMessage}</span>
        )}
        {saveError && <span className="text-sm text-cf-danger">{saveError}</span>}
        {saved && <span className="text-sm text-cf-success">{t('admin.pricing.published')}</span>}
      </section>

      <ConfirmDialog
        open={confirmingRecommended}
        title={t('admin.pricing.recommendedTitle')}
        confirmLabel={t('admin.pricing.recommended')}
        onCancel={() => setConfirmingRecommended(false)}
        onConfirm={() => {
          applyRecommendedGrid();
          setConfirmingRecommended(false);
        }}
        message={t('admin.pricing.recommendedConfirm')}
      />
    </div>
  );
}

export default PricingPlansPage;
